import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import {
  Phone, Mail, Globe, MapPin, Plus, Loader2, Trash2, Star, Sparkles,
  Copy, X, Search, Filter, FileText, Upload, Download, Paperclip,
  Send, Inbox, Compass, MessageCircle, GripVertical, Check, AlertCircle,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable,
  DragOverlay,
} from '@dnd-kit/core';

const CATEGORIES = [
  'Venue', 'Catering', 'Photography', 'Videography', 'Florist',
  'Music / DJ', 'Band', 'Officiant', 'Cake / Dessert', 'Attire',
  'Beauty', 'Rentals', 'Stationery', 'Transportation', 'Coordinator', 'Other',
];

const STATUSES = [
  { key: 'Researching', color: 'bg-stone-100 text-stone-700 border-stone-200', accent: '#9A8A7E' },
  { key: 'Contacted',   color: 'bg-sky-50 text-sky-700 border-sky-200', accent: '#0284c7' },
  { key: 'Considering', color: 'bg-amber-50 text-amber-700 border-amber-200', accent: '#b45309' },
  { key: 'Quote in',    color: 'bg-indigo-50 text-indigo-700 border-indigo-200', accent: '#4338ca' },
  { key: 'Booked',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200', accent: '#047857' },
  { key: 'Paid',        color: 'bg-[#F4E4DA] text-[#7B2C3B] border-[#EBD3C2]', accent: '#7B2C3B' },
];
const STATUS_KEYS = STATUSES.map(s => s.key);
const legacyStatusMap = { Pending: 'Researching', Booked: 'Booked', Considering: 'Considering' };
const statusColor = (s) => (STATUSES.find(x => x.key === s) || STATUSES[0]).color;

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [unread, setUnread] = useState({});
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('pipeline');
  const [filterCat, setFilterCat] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [aiTarget, setAiTarget] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [autoAction, setAutoAction] = useState(null);
  const [showDiscover, setShowDiscover] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    const [{ data: vs }, { data: un }] = await Promise.all([
      client.get('/vendors'),
      client.get('/vendors/unread-counts'),
    ]);
    setVendors(vs.map(v => ({ ...v, status: legacyStatusMap[v.status] || v.status || 'Researching' })));
    setUnread(un || {});
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (loading) return;
    const url = new URL(window.location.href);
    const open = url.searchParams.get('open');
    if (open && vendors.some(v => v.id === open)) {
      setEditing(open);
      url.searchParams.delete('open');
      window.history.replaceState(null, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const update = async (id, patch) => {
    setVendors((prev) => prev.map(v => v.id === id ? { ...v, ...patch } : v));
    try { await client.put(`/vendors/${id}`, patch); } catch { /* ignore */ }
  };

  const addVendor = async () => {
    const { data } = await client.post('/vendors', {});
    setVendors((prev) => [{ ...data, status: legacyStatusMap[data.status] || data.status || 'Researching' }, ...prev]);
    setEditing(data.id);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this vendor? This can't be undone.")) return;
    setVendors((prev) => prev.filter(v => v.id !== id));
    try { await client.delete(`/vendors/${id}`); } catch { /* ignore */ }
  };

  const moveStatus = async (vendor, toStatus) => {
    const fromStatus = vendor.status || 'Researching';
    if (fromStatus === toStatus) return;
    setVendors((prev) => prev.map(v => v.id === vendor.id ? { ...v, status: toStatus } : v));
    try {
      const { data } = await client.post(`/vendors/${vendor.id}/move-status`, { from_status: fromStatus, to_status: toStatus });
      if (data.direction === 'forward' && data.next_action) {
        setAutoAction({ vendor: { ...vendor, status: toStatus }, ...data.next_action });
      } else {
        toast({ title: `Moved to ${toStatus}` });
      }
    } catch (e) {
      setVendors((prev) => prev.map(v => v.id === vendor.id ? { ...v, status: fromStatus } : v));
      toast({ title: 'Move failed', description: e?.response?.data?.detail || 'Try again in a moment', variant: 'destructive' });
    }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return vendors.filter(v => {
      if (filterCat !== 'All' && v.category !== filterCat) return false;
      if (filterStatus !== 'All' && (v.status || 'Researching') !== filterStatus) return false;
      if (query) {
        const hay = `${v.name || ''} ${v.contact || ''} ${v.notes || ''} ${v.category || ''}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [vendors, filterCat, filterStatus, q]);

  const totals = useMemo(() => {
    const t = { count: vendors.length, booked: 0, paid: 0, total: 0, dueSoon: 0, unreadTotal: 0 };
    const now = Date.now();
    const soon = now + 30 * 86400e3;
    vendors.forEach(v => {
      if (v.status === 'Booked' || v.status === 'Paid') t.booked += 1;
      t.paid += Number(v.paid) || 0;
      t.total += Number(v.total) || 0;
      if (v.final_payment_due) {
        const d = Date.parse(v.final_payment_due);
        if (d && d > now && d < soon && (Number(v.total) || 0) > (Number(v.paid) || 0)) t.dueSoon += 1;
      }
    });
    t.unreadTotal = Object.values(unread).reduce((a, b) => a + b, 0);
    return t;
  }, [vendors, unread]);

  const toggleCompare = (id) => {
    setCompareIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : (prev.length < 4 ? [...prev, id] : prev));
  };

  const runCompare = async () => {
    if (compareIds.length < 2) return;
    try {
      const { data } = await client.post('/vendors/ai/compare', { vendor_ids: compareIds });
      setCompareResult(data);
    } catch (e) {
      setCompareResult({ summary: e?.response?.data?.detail || 'Compare failed.', vendors: [] });
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const onDragStart = (e) => setDraggingId(e.active.id);
  const onDragEnd = (e) => {
    setDraggingId(null);
    const { active, over } = e;
    if (!over) return;
    const toStatus = String(over.id).replace(/^col-/, '');
    if (!STATUS_KEYS.includes(toStatus)) return;
    const vendor = vendors.find(v => v.id === active.id);
    if (vendor) moveStatus(vendor, toStatus);
  };

  const draggingVendor = vendors.find(v => v.id === draggingId);

  if (loading) return <div className="flex items-center gap-2 text-[#6A6A6A]"><Loader2 className="w-4 h-4 animate-spin" /> Loading vendors…</div>;

  return (
    <div className="animate-float-up">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-4xl text-[#2A2A2A] mb-1 flex items-center gap-3"><AppIcon feature="vendors" size="lg" reveal testId="section-icon-vendors" />Vendor hub</h1>
          <p className="text-[#6A6A6A]">Shortlist, message, book — drag between stages and the AI takes care of the next step.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowDiscover(true)} data-testid="vendors-discover-btn" className="text-xs px-3 py-1.5 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white inline-flex items-center gap-1.5">
            <Compass className="w-3.5 h-3.5" /> Discover vendors
          </button>
          <div className="inline-flex rounded-full border border-[#EFE3D8] p-0.5 bg-white text-xs">
            <button onClick={() => setView('cards')} data-testid="vendors-view-cards" className={`px-3 py-1.5 rounded-full ${view === 'cards' ? 'bg-[#7B2C3B] text-[#FBF7F2]' : 'text-[#3A3A3A]'}`}>Cards</button>
            <button onClick={() => setView('pipeline')} data-testid="vendors-view-pipeline" className={`px-3 py-1.5 rounded-full ${view === 'pipeline' ? 'bg-[#7B2C3B] text-[#FBF7F2]' : 'text-[#3A3A3A]'}`}>Pipeline</button>
          </div>
          <button onClick={addVendor} data-testid="vendors-add" className="btn-primary text-sm px-4 py-2 rounded-full inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add vendor</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Stat label="Vendors" value={totals.count} />
        <Stat label="Booked" value={totals.booked} />
        <Stat label="Paid" value={`$${totals.paid.toLocaleString()}`} sub={`of $${totals.total.toLocaleString()}`} />
        <Stat label="Payments due (30d)" value={totals.dueSoon} accent={totals.dueSoon > 0} />
        <Stat label="Unread replies" value={totals.unreadTotal} accent={totals.unreadTotal > 0} icon={<Inbox className="w-3 h-3" />} />
      </div>

      <div className="soft-card rounded-2xl p-3 mb-5 bg-white border border-[#EFE3D8] flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#B8896C]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors, contacts, notes…"
                 data-testid="vendors-search" className="flex-1 bg-transparent text-sm focus:outline-none" />
        </div>
        <div className="inline-flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-[#B8896C]" />
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="vendors-filter-cat"
                  className="text-xs px-2 py-1 rounded-full border border-[#EFE3D8] bg-white">
            <option value="All">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="vendors-filter-status"
                  className="text-xs px-2 py-1 rounded-full border border-[#EFE3D8] bg-white">
            <option value="All">All statuses</option>
            {STATUSES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
          </select>
        </div>
        {compareIds.length >= 2 && (
          <button onClick={runCompare} data-testid="vendors-compare-run"
                  className="btn-primary text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Compare {compareIds.length} with AI
          </button>
        )}
      </div>

      {vendors.length === 0 ? (
        <div className="soft-card rounded-2xl p-10 text-center text-[#6A6A6A]">
          No vendors yet.
          <div className="mt-4 flex items-center justify-center gap-2">
            <button onClick={addVendor} className="btn-primary text-xs px-4 py-2 rounded-full">+ Add manually</button>
            <button onClick={() => setShowDiscover(true)} className="text-xs px-4 py-2 rounded-full border border-[#7B2C3B] text-[#7B2C3B] inline-flex items-center gap-1.5"><Compass className="w-3.5 h-3.5" /> Discover with AI</button>
          </div>
        </div>
      ) : view === 'pipeline' ? (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {STATUSES.map(col => (
              <PipelineColumn key={col.key} col={col} vendors={filtered.filter(v => (v.status || 'Researching') === col.key)}
                              unread={unread} onOpen={setEditing} isDragging={!!draggingId} />
            ))}
          </div>
          <DragOverlay>
            {draggingVendor ? <VendorMiniCard v={draggingVendor} unread={unread[draggingVendor.id] || 0} floating /> : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((v) => (
            <VendorCard
              key={v.id} v={v} unread={unread[v.id] || 0}
              onEdit={() => setEditing(v.id)}
              onAI={() => setAiTarget({ vendor: v, text: '', channel: 'email' })}
              onRemove={() => remove(v.id)}
              onToggleFav={() => update(v.id, { favorite: !v.favorite })}
              onQuickStatus={(s) => moveStatus(v, s)}
              compareOn={compareIds.includes(v.id)}
              onToggleCompare={() => toggleCompare(v.id)}
            />
          ))}
        </div>
      )}

      {editing && (
        <VendorEditor
          v={vendors.find(x => x.id === editing)}
          onClose={() => { setEditing(null); load(); }}
          onSave={(patch) => update(editing, patch)}
          onAI={() => { const vv = vendors.find(x => x.id === editing); setAiTarget({ vendor: vv, text: '', channel: 'email' }); }}
          unread={unread[editing] || 0}
          onMessagesRead={() => setUnread((u) => ({ ...u, [editing]: 0 }))}
        />
      )}
      {aiTarget && (
        <AIOutreachDrawer state={aiTarget}
                          onClose={() => setAiTarget(null)}
                          onGenerated={(text, channel) => setAiTarget((t) => ({ ...t, text, channel }))}
                          onSent={() => { load(); }} />
      )}
      {compareResult && <ComparePanel result={compareResult} onClose={() => { setCompareResult(null); setCompareIds([]); }} />}
      {autoAction && (
        <AutoActionModal
          state={autoAction}
          onClose={() => setAutoAction(null)}
          onOpenEditor={(vid) => { setAutoAction(null); setEditing(vid); }}
          onOpenAI={(vendor) => { setAutoAction(null); setAiTarget({ vendor, text: '', channel: 'email' }); }}
          onDone={() => { setAutoAction(null); load(); }}
        />
      )}
      {showDiscover && <DiscoverModal onClose={() => setShowDiscover(false)} onAdded={() => { setShowDiscover(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value, sub, accent, icon }) {
  return (
    <div className={`soft-card rounded-2xl px-4 py-3 border ${accent ? 'bg-[#FFF7EC] border-[#F5D9B7]' : 'bg-white border-[#EFE3D8]'}`}>
      <p className="text-[10px] uppercase tracking-widest text-[#9A8A7E] inline-flex items-center gap-1">{icon}{label}</p>
      <p className="font-serif text-2xl text-[#2A2A2A]">{value}</p>
      {sub && <p className="text-[11px] text-[#9A8A7E]">{sub}</p>}
    </div>
  );
}

function PipelineColumn({ col, vendors, unread, onOpen, isDragging }) {
  const { isOver, setNodeRef } = useDroppable({ id: `col-${col.key}` });
  const columnUnread = vendors.reduce((a, v) => a + (unread[v.id] || 0), 0);
  return (
    <div ref={setNodeRef} data-testid={`pipeline-col-${col.key}`}
         className={`soft-card rounded-2xl p-3 border transition-colors ${isOver ? 'border-[#7B2C3B] bg-white ring-2 ring-[#7B2C3B]/10' : 'border-[#EFE3D8] bg-[#FBF7F2]'}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-widest" style={{ color: col.accent }}>
          {col.key} <span className="text-[#9A8A7E]">· {vendors.length}</span>
        </p>
        {columnUnread > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#7B2C3B] text-white font-medium">{columnUnread}</span>
        )}
      </div>
      <div className="space-y-2 min-h-[60px]">
        {vendors.map(v => (
          <DraggableCard key={v.id} v={v} onOpen={onOpen} unread={unread[v.id] || 0} />
        ))}
        {vendors.length === 0 && !isDragging && (
          <p className="text-[11px] text-[#B8896C] italic text-center py-3">Drop vendors here</p>
        )}
      </div>
    </div>
  );
}

function DraggableCard({ v, onOpen, unread }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: v.id });
  return (
    <div ref={setNodeRef} {...attributes}
         className={`bg-white rounded-xl border p-2.5 transition-all ${isDragging ? 'opacity-30 border-[#7B2C3B]' : 'border-[#EFE3D8] hover:border-[#B8896C]'} ${unread > 0 ? 'ring-1 ring-[#7B2C3B]/30' : ''}`}
         data-testid={`vendors-pipeline-card-${v.id}`}>
      <div className="flex items-start justify-between gap-1">
        <button {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 -m-0.5 text-[#B8896C] hover:text-[#7B2C3B]" title="Drag to move">
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        {unread > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#7B2C3B] text-white inline-flex items-center gap-1">
            <Inbox className="w-2.5 h-2.5" /> {unread}
          </span>
        )}
      </div>
      <button onClick={() => onOpen(v.id)} className="w-full text-left mt-0.5">
        <p className="text-[10px] uppercase tracking-wider text-[#B8896C]">{v.category || 'Other'}</p>
        <p className="text-sm font-medium text-[#2A2A2A] truncate">{v.name}</p>
        {(v.total > 0) && (
          <div className="mt-1.5 h-1 rounded-full bg-[#F4E4DA] overflow-hidden">
            <div className="h-full bg-[#7B2C3B]" style={{ width: `${Math.min(100, Math.round(((v.paid || 0) / v.total) * 100))}%` }} />
          </div>
        )}
      </button>
    </div>
  );
}

function VendorMiniCard({ v, unread, floating }) {
  return (
    <div className={`bg-white rounded-xl border border-[#7B2C3B] p-2.5 shadow-xl ${floating ? 'rotate-2' : ''}`} style={{ width: 220 }}>
      <p className="text-[10px] uppercase tracking-wider text-[#B8896C]">{v.category || 'Other'}</p>
      <p className="text-sm font-medium text-[#2A2A2A] truncate">{v.name}</p>
      {unread > 0 && <span className="text-[10px] mt-1 inline-block px-1.5 py-0.5 rounded-full bg-[#7B2C3B] text-white">{unread} new</span>}
    </div>
  );
}

function VendorCard({ v, unread, onEdit, onAI, onRemove, onToggleFav, onQuickStatus, compareOn, onToggleCompare }) {
  const pct = v.total ? Math.min(100, Math.round(((v.paid || 0) / v.total) * 100)) : 0;
  const status = v.status || 'Researching';
  return (
    <div data-testid={`vendor-card-${v.id}`} className={`soft-card rounded-2xl p-5 border ${compareOn ? 'border-[#7B2C3B] ring-2 ring-[#7B2C3B]/20' : 'border-transparent'} hover:translate-y-[-2px] transition-transform relative`}>
      {unread > 0 && (
        <span className="absolute -top-1.5 -right-1.5 text-[11px] px-2 py-0.5 rounded-full bg-[#7B2C3B] text-white shadow inline-flex items-center gap-1">
          <Inbox className="w-3 h-3" /> {unread} new
        </span>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-widest text-[#B8896C]">{v.category || 'Other'}</p>
          <div className="flex items-center gap-1.5">
            <button onClick={onToggleFav} data-testid={`vendor-fav-${v.id}`} className="flex-shrink-0" title="Favorite">
              <Star className={`w-4 h-4 ${v.favorite ? 'text-amber-400 fill-amber-400' : 'text-[#D4C4B8]'}`} />
            </button>
            <button onClick={onEdit} className="font-serif text-xl text-[#2A2A2A] truncate text-left">{v.name}</button>
          </div>
        </div>
        <select value={status} onChange={(e) => onQuickStatus(e.target.value)}
                data-testid={`vendor-status-${v.id}`}
                className={`text-[11px] px-2 py-1 rounded-full border cursor-pointer flex-shrink-0 ${statusColor(status)}`}>
          {STATUSES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
        </select>
      </div>
      <div className="text-sm text-[#3A3A3A] space-y-1">
        {v.contact && <p className="truncate">{v.contact}</p>}
        {v.phone && <p className="flex items-center gap-1.5 text-xs"><Phone className="w-3 h-3 text-[#B8896C]" /> <a href={`tel:${v.phone}`} className="hover:underline">{v.phone}</a></p>}
        {v.email && <p className="flex items-center gap-1.5 text-xs"><Mail className="w-3 h-3 text-[#B8896C]" /> <a href={`mailto:${v.email}`} className="hover:underline truncate">{v.email}</a></p>}
        {v.website && <p className="flex items-center gap-1.5 text-xs"><Globe className="w-3 h-3 text-[#B8896C]" /> <a href={/^https?:/.test(v.website) ? v.website : `https://${v.website}`} target="_blank" rel="noreferrer" className="hover:underline truncate">{v.website}</a></p>}
        {v.address && <p className="flex items-center gap-1.5 text-xs"><MapPin className="w-3 h-3 text-[#B8896C]" /> <span className="truncate">{v.address}</span></p>}
      </div>
      {(v.total > 0 || v.paid > 0) && (
        <div className="mt-3 pt-3 border-t border-[#EFE3D8]">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-[#6A6A6A]">Paid <strong className="text-[#2A2A2A]">${(v.paid || 0).toLocaleString()}</strong> / ${(v.total || 0).toLocaleString()}</span>
            <span className="text-[#9A8A7E]">{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#F4E4DA] overflow-hidden">
            <div className="h-full rounded-full bg-[#7B2C3B]" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={onAI} data-testid={`vendor-ai-${v.id}`}
                className="text-xs px-3 py-1.5 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Draft outreach
        </button>
        <button onClick={onEdit} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] text-[#3A3A3A] hover:border-[#B8896C]">Edit</button>
        {(v.attachments || []).length > 0 && (
          <button onClick={onEdit} className="text-xs px-2.5 py-1.5 rounded-full border border-[#EFE3D8] text-[#7B2C3B] hover:border-[#B8896C] inline-flex items-center gap-1" title="Contracts & documents">
            <Paperclip className="w-3 h-3" /> {v.attachments.length}
          </button>
        )}
        <label className="ml-auto text-xs inline-flex items-center gap-1 text-[#7B2C3B] cursor-pointer">
          <input type="checkbox" checked={compareOn} onChange={onToggleCompare} data-testid={`vendor-compare-${v.id}`} className="accent-[#7B2C3B]" />
          Compare
        </label>
        <button onClick={onRemove} className="text-[#B8896C] hover:text-red-600" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

// ---------- Vendor editor (with conversation tab + create-budget-cat) ----------
function VendorEditor({ v, onClose, onSave, onAI, unread, onMessagesRead }) {
  const [tab, setTab] = useState('details');
  const [budget, setBudget] = useState({ total: 0, categories: [] });
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/budget');
        setBudget(data);
      } catch { /* ignore */ }
    })();
  }, []);

  // Auto-mark-read + load messages when Messages tab opens
  useEffect(() => {
    if (tab !== 'messages' || !v) return;
    (async () => {
      setMsgLoading(true);
      try {
        const { data } = await client.get(`/vendors/${v.id}/messages`);
        setMessages(data);
        if (unread > 0) {
          await client.post(`/vendors/${v.id}/messages/mark-read`);
          onMessagesRead?.();
        }
      } finally { setMsgLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, v?.id]);

  const budgetCats = budget.categories || [];
  const upload = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await client.post('/uploads/document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const next = [...(v.attachments || []), {
        id: data.filename, name: data.originalName || file.name, url: data.url,
        content_type: data.contentType, size: data.size, uploaded_at: new Date().toISOString(),
      }];
      onSave({ attachments: next });
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Upload failed.');
    }
  };
  const removeAttachment = (id) => onSave({ attachments: (v.attachments || []).filter(a => a.id !== id) });

  const createBudgetCat = async () => {
    const name = window.prompt('New budget category name?', v.category || 'New category');
    if (!name) return;
    try {
      const { data } = await client.post('/budget/categories', { name, allocated: 500, color: '#D9B8A6' });
      setBudget((b) => ({ ...b, categories: [...(b.categories || []), data] }));
      onSave({ budget_category: name });
      toast({ title: `Created "${name}" — vendor rolls into it now.` });
    } catch (e) {
      toast({ title: 'Could not create category', variant: 'destructive' });
    }
  };

  if (!v) return null;
  const set = (field) => (e) => onSave({ [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const setNum = (field) => (e) => onSave({ [field]: Number(e.target.value) || 0 });
  const hasBudgetCatMatch = !!(v.budget_category && budgetCats.some(c => c.name === v.budget_category));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="vendor-editor-backdrop">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between flex-shrink-0">
          <h2 className="font-serif text-xl text-[#2A2A2A]">{v.name || 'Vendor'}</h2>
          <button onClick={onClose} data-testid="vendor-editor-close" className="text-[#9A8A7E] hover:text-[#7B2C3B]"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex border-b border-[#EFE3D8] px-2 gap-1 flex-shrink-0">
          <TabBtn active={tab === 'details'} onClick={() => setTab('details')} testid="vendor-tab-details">Details</TabBtn>
          <TabBtn active={tab === 'messages'} onClick={() => setTab('messages')} testid="vendor-tab-messages" badge={unread}>
            <MessageCircle className="w-3.5 h-3.5" /> Messages
          </TabBtn>
          <TabBtn active={tab === 'money'} onClick={() => setTab('money')} testid="vendor-tab-money">Quote & payments</TabBtn>
        </div>

        {tab === 'details' && (
          <div className="p-5 space-y-3 overflow-y-auto">
            <Row label="Name"><input value={v.name || ''} onChange={set('name')} className="input" data-testid="vendor-edit-name" /></Row>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Category">
                <select value={v.category || 'Other'} onChange={set('category')} className="input" data-testid="vendor-edit-cat">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Row>
              <Row label="Status">
                <select value={v.status || 'Researching'} onChange={set('status')} className="input" data-testid="vendor-edit-status">
                  {STATUSES.map(s => <option key={s.key} value={s.key}>{s.key}</option>)}
                </select>
              </Row>
            </div>
            <Row label="Budget category (rolls into your Budget tab)">
              <div className="flex items-center gap-2">
                <select value={v.budget_category || ''} onChange={set('budget_category')} className="input flex-1" data-testid="vendor-edit-budget-cat">
                  <option value="">— not tracked in budget —</option>
                  {budgetCats.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                </select>
                <button type="button" onClick={createBudgetCat} data-testid="vendor-create-budget-cat"
                        className="text-xs px-2.5 py-1.5 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white inline-flex items-center gap-1 whitespace-nowrap">
                  <Plus className="w-3 h-3" /> New
                </button>
              </div>
              {v.budget_category && !hasBudgetCatMatch && (
                <p className="text-[10px] text-[#7B4A1A] mt-1 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> "{v.budget_category}" doesn't exist on your Budget tab yet — click "New" to add it.
                </p>
              )}
            </Row>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Contact name"><input value={v.contact || ''} onChange={set('contact')} className="input" /></Row>
              <Row label="Phone"><input value={v.phone || ''} onChange={set('phone')} className="input" placeholder="+15551234567 (E.164 for SMS)" /></Row>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Email"><input type="email" value={v.email || ''} onChange={set('email')} className="input" /></Row>
              <Row label="Website"><input value={v.website || ''} onChange={set('website')} className="input" placeholder="https://" /></Row>
            </div>
            <Row label="Address"><input value={v.address || ''} onChange={set('address')} className="input" /></Row>
            <div className="grid grid-cols-3 gap-3">
              <Row label="Deposit ($)"><input type="number" value={v.deposit || 0} onChange={setNum('deposit')} className="input" /></Row>
              <Row label="Paid ($)"><input type="number" value={v.paid || 0} onChange={setNum('paid')} className="input" /></Row>
              <Row label="Total ($)"><input type="number" value={v.total || 0} onChange={setNum('total')} className="input" /></Row>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Contract signed on"><input type="date" value={v.contract_signed_at || ''} onChange={set('contract_signed_at')} className="input" /></Row>
              <Row label="Final payment due"><input type="date" value={v.final_payment_due || ''} onChange={set('final_payment_due')} className="input" /></Row>
            </div>
            <Row label="Notes"><textarea value={v.notes || ''} onChange={set('notes')} rows={3} className="input resize-none" placeholder="What we liked, questions to follow up, package details, red flags…" /></Row>

            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#9A8A7E] mb-1.5 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> Contracts & documents</p>
              <label className="block cursor-pointer border-2 border-dashed border-[#EFE3D8] rounded-xl p-3 text-center hover:border-[#B8896C] transition-colors" data-testid="vendor-upload-doc-label">
                <input type="file" accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                       onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }}
                       className="hidden" data-testid="vendor-upload-doc-input" />
                <Upload className="w-5 h-5 mx-auto text-[#B8896C] mb-1" />
                <p className="text-xs text-[#7B2C3B] font-medium">Upload contract, invoice or quote</p>
                <p className="text-[10px] text-[#9A8A7E]">PDF, DOCX or image · up to 15 MB</p>
              </label>
              {(v.attachments || []).length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="vendor-attachments">
                  {v.attachments.map((a) => (
                    <li key={a.id} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-[#EFE3D8] bg-[#FBF7F2] text-xs">
                      <FileText className="w-3.5 h-3.5 text-[#7B2C3B] flex-shrink-0" />
                      <a href={a.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-[#2A2A2A] hover:underline">{a.name}</a>
                      <span className="text-[10px] text-[#9A8A7E]">{Math.round((a.size || 0) / 1024)} KB</span>
                      <a href={a.url} download={a.name} className="text-[#B8896C] hover:text-[#7B2C3B]" title="Download"><Download className="w-3.5 h-3.5" /></a>
                      <button onClick={() => removeAttachment(a.id)} className="text-[#B8896C] hover:text-red-600" title="Remove"><X className="w-3.5 h-3.5" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === 'messages' && (
          <ConversationLog v={v} messages={messages} loading={msgLoading} onOpenAI={onAI} onRefresh={async () => {
            const { data } = await client.get(`/vendors/${v.id}/messages`); setMessages(data);
          }} />
        )}

        {tab === 'money' && (
          <MoneyTab v={v} onSaved={() => onSave({})} />
        )}

        <div className="p-3 border-t border-[#EFE3D8] flex items-center justify-between gap-2 flex-shrink-0 bg-white">
          <button onClick={onAI} data-testid="vendor-editor-draft-outreach" className="text-xs px-4 py-2 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Draft outreach
          </button>
          <button onClick={onClose} className="btn-primary text-xs px-4 py-2 rounded-full">Done</button>
        </div>
      </div>
      <style>{`.input{width:100%;padding:0.5rem 0.75rem;border-radius:0.75rem;border:1px solid #EFE3D8;font-size:0.875rem;background:#FBF7F2}.input:focus{outline:none;border-color:#7B2C3B}`}</style>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid, badge }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`px-3 py-2 text-xs font-medium inline-flex items-center gap-1 border-b-2 -mb-px ${active ? 'border-[#7B2C3B] text-[#7B2C3B]' : 'border-transparent text-[#6A6A6A] hover:text-[#2A2A2A]'}`}>
      {children}
      {badge > 0 && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#7B2C3B] text-white">{badge}</span>}
    </button>
  );
}

function ConversationLog({ v, messages, loading, onOpenAI, onRefresh }) {
  return (
    <div className="p-5 overflow-y-auto flex-1" data-testid="vendor-conversation">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg text-[#2A2A2A]">Conversation</h3>
        <button onClick={onOpenAI} className="text-xs px-3 py-1.5 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> New message
        </button>
      </div>
      {loading ? (
        <p className="text-sm text-[#6A6A6A] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : messages.length === 0 ? (
        <p className="text-sm text-[#9A8A7E] italic text-center py-6">
          No messages yet. Click "New message" to send them an email or SMS — replies show up here automatically.
        </p>
      ) : (
        <ul className="space-y-3">
          {messages.map((m) => (
            <li key={m.id} className={`p-3 rounded-xl border ${m.direction === 'out' ? 'bg-[#F4E4DA]/40 border-[#EBD3C2] ml-8' : 'bg-white border-[#EFE3D8] mr-8'}`}
                data-testid={`vendor-msg-${m.id}`}>
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#9A8A7E] mb-1">
                <span>{m.direction === 'out' ? 'You → ' + (v.name) : (v.name) + ' → you'} · {m.channel}</span>
                <span>{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</span>
              </div>
              {m.subject && <p className="text-xs font-medium text-[#2A2A2A] mb-1">{m.subject}</p>}
              <pre className="whitespace-pre-wrap font-sans text-sm text-[#2A2A2A] leading-relaxed">{m.body}</pre>
              {m.status === 'failed' && <p className="text-[11px] text-red-600 mt-2">Failed: {m.error}</p>}
              {m.status === 'mocked' && <p className="text-[11px] text-amber-700 mt-2">MOCKED — provider not configured, message wasn't actually sent.</p>}
            </li>
          ))}
        </ul>
      )}
      <button onClick={onRefresh} className="mt-4 text-xs text-[#7B2C3B] hover:underline">↻ Refresh</button>
    </div>
  );
}

function MoneyTab({ v, onSaved }) {
  const [quote, setQuote] = useState({ total: v.total || 0, deposit: v.deposit || 0, inclusions: '', valid_until: '', notes: '' });
  const [payment, setPayment] = useState({ amount: 0, note: '', is_final: false });
  const [busy, setBusy] = useState(false);
  const [fit, setFit] = useState(null);
  const { toast } = useToast();

  const logQuote = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/vendors/${v.id}/log-quote`, quote);
      setFit(data.budget_fit);
      toast({ title: 'Quote saved to this vendor.' });
      onSaved?.();
    } catch (e) {
      toast({ title: 'Could not save quote', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };
  const logPayment = async () => {
    if (payment.amount <= 0) return;
    setBusy(true);
    try {
      await client.post(`/vendors/${v.id}/log-payment`, payment);
      toast({ title: `Payment logged. ${payment.is_final ? 'Marked as Paid.' : ''}` });
      setPayment({ amount: 0, note: '', is_final: false });
      onSaved?.();
    } catch (e) {
      toast({ title: 'Could not log payment', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="p-5 space-y-5 overflow-y-auto flex-1" data-testid="vendor-money-tab">
      <div>
        <h3 className="font-serif text-lg text-[#2A2A2A] mb-2">Log a quote</h3>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Total ($)"><input type="number" value={quote.total} onChange={(e) => setQuote({ ...quote, total: Number(e.target.value) || 0 })} className="input" data-testid="quote-total" /></Row>
          <Row label="Deposit ($)"><input type="number" value={quote.deposit} onChange={(e) => setQuote({ ...quote, deposit: Number(e.target.value) || 0 })} className="input" /></Row>
          <Row label="Valid until"><input type="date" value={quote.valid_until} onChange={(e) => setQuote({ ...quote, valid_until: e.target.value })} className="input" /></Row>
          <Row label="Inclusions"><input value={quote.inclusions} onChange={(e) => setQuote({ ...quote, inclusions: e.target.value })} className="input" placeholder="Bouquet, centerpieces…" /></Row>
        </div>
        <Row label="Notes"><textarea value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} className="input resize-none" rows={2} /></Row>
        <button onClick={logQuote} disabled={busy} data-testid="quote-save" className="mt-2 btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save quote
        </button>
        {fit && (
          <div className={`mt-3 p-3 rounded-xl text-xs ${fit.verdict === 'within_budget' ? 'bg-[#EFF6EF] text-[#3F6B3F]' : 'bg-[#FFF7EC] text-[#7B4A1A]'}`}>
            <strong>{fit.category}:</strong> quote ${fit.quote_total.toLocaleString()} vs ${fit.remaining.toLocaleString()} remaining.
            {fit.verdict === 'over_budget' && ` Over by $${Math.abs(fit.overshoot).toLocaleString()}.`}
          </div>
        )}
      </div>

      <div className="border-t border-[#EFE3D8] pt-4">
        <h3 className="font-serif text-lg text-[#2A2A2A] mb-2">Log a payment</h3>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Amount ($)"><input type="number" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: Number(e.target.value) || 0 })} className="input" data-testid="payment-amount" /></Row>
          <Row label="Note"><input value={payment.note} onChange={(e) => setPayment({ ...payment, note: e.target.value })} className="input" placeholder="Deposit, final invoice…" /></Row>
        </div>
        <label className="flex items-center gap-2 mt-2 text-xs text-[#3A3A3A]">
          <input type="checkbox" checked={payment.is_final} onChange={(e) => setPayment({ ...payment, is_final: e.target.checked })} className="accent-[#7B2C3B]" />
          Final payment (mark as Paid)
        </label>
        <button onClick={logPayment} disabled={busy || payment.amount <= 0} data-testid="payment-save" className="mt-2 btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Log payment
        </button>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// ---------- AI Outreach drawer with real Send + SMS gating ----------
function AIOutreachDrawer({ state, onClose, onGenerated, onSent }) {
  const { vendor, text, channel } = state;
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);
  const [error, setError] = useState('');
  const [tone, setTone] = useState('warm');
  const [ch, setCh] = useState(channel || 'email');
  const [subject, setSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    if (!text) { setSubject(''); setEditedBody(''); return; }
    if (ch === 'email') {
      const m = /^\s*SUBJECT:\s*(.+?)\n(.*)$/is.exec(text || '');
      if (m) { setSubject(m[1].trim()); setEditedBody(m[2].trim()); }
      else { setSubject('Wedding inquiry'); setEditedBody(text || ''); }
    } else {
      setSubject('');
      setEditedBody(text || '');
    }
  }, [text, ch]);

  const generate = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await client.post(`/vendors/${vendor.id}/ai/outreach`, { channel: ch, tone });
      onGenerated(data.text, data.channel);
    } catch (e) {
      setError(`Couldn't reach the AI writer: ${e?.response?.data?.detail || 'try again in a moment'}.`);
    } finally { setBusy(false); }
  };

  const send = async () => {
    setSending(true); setError('');
    try {
      const body = ch === 'email'
        ? { channel: 'email', subject, body: editedBody }
        : { channel: 'sms', body: editedBody };
      const { data } = await client.post(`/vendors/${vendor.id}/messages/send`, body);
      const s = data?.message?.status;
      setSentInfo({ status: s, mocked: data?.result?.mocked, error: data?.result?.error });
      if (s === 'sent') toast({ title: `${ch === 'email' ? 'Email' : 'SMS'} sent to ${vendor.name}` });
      else if (s === 'mocked') toast({ title: `Message logged (provider not configured — MOCKED)` });
      else toast({ title: 'Send failed', description: data?.result?.error || '', variant: 'destructive' });
      onSent?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'plan_limit_reached' && detail?.limit_type === 'sms_sends') {
        setError(`SMS requires the Premium plan or higher. ${detail.message || ''}`);
      } else {
        setError(typeof detail === 'string' ? detail : (detail?.message || 'Send failed. Try again.'));
      }
    } finally { setSending(false); }
  };

  const copy = () => { navigator.clipboard.writeText(editedBody || ''); toast({ title: 'Copied' }); };

  const canSend = ch === 'email' ? (vendor.email && editedBody.trim()) : (vendor.phone && editedBody.trim());

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="vendor-ai-drawer">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between">
          <div>
            <h2 className="font-serif text-lg text-[#2A2A2A] inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#7B2C3B]" /> Message {vendor.name}</h2>
            <p className="text-xs text-[#6A6A6A]">Claude drafts a warm, on-brand message. Edit, then send from right here — the reply lands in this vendor's Messages tab.</p>
          </div>
          <button onClick={onClose} className="text-[#9A8A7E] hover:text-[#7B2C3B]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b border-[#EFE3D8] bg-[#FBF7F2] flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Channel</span>
          {['email', 'sms'].map(c => (
            <button key={c} onClick={() => setCh(c)} data-testid={`vendor-ai-ch-${c}`} className={`text-xs px-3 py-1 rounded-full border ${ch === c ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8] text-[#3A3A3A]'}`}>{c === 'email' ? 'Email' : 'Text (SMS)'}</button>
          ))}
          <span className="ml-3 text-[10px] uppercase tracking-widest text-[#9A8A7E]">Tone</span>
          {['warm', 'formal', 'quick'].map(t => (
            <button key={t} onClick={() => setTone(t)} className={`text-xs px-3 py-1 rounded-full border capitalize ${tone === t ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8] text-[#3A3A3A]'}`}>{t}</button>
          ))}
          <button onClick={generate} disabled={busy} data-testid="vendor-ai-generate" className="ml-auto btn-primary text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {busy ? 'Writing…' : (text ? 'Regenerate' : 'Generate')}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {!text && !busy && <p className="text-sm text-[#6A6A6A] py-10 text-center">Pick a channel + tone, then hit <em>Generate</em>.</p>}
          {busy && <p className="text-sm text-[#6A6A6A] py-10 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Drafting your message…</p>}
          {!busy && text && (
            <>
              {ch === 'email' && (
                <Row label="Subject">
                  <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="vendor-ai-subject" className="input" />
                </Row>
              )}
              <Row label={ch === 'email' ? 'Body' : 'Text message'}>
                <textarea value={editedBody} onChange={(e) => setEditedBody(e.target.value)} rows={ch === 'email' ? 12 : 5}
                          data-testid="vendor-ai-output" className="input resize-none font-mono" />
              </Row>
              {ch === 'email' && !vendor.email && <p className="text-xs text-[#7B4A1A] inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Add an email to this vendor on the Details tab before sending.</p>}
              {ch === 'sms' && !vendor.phone && <p className="text-xs text-[#7B4A1A] inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Add a phone number (E.164 format, e.g. +15551234567) before sending.</p>}
              {error && <p className="text-xs text-red-600" data-testid="vendor-ai-error">{error}</p>}
              {sentInfo && (
                <div className={`text-xs p-2.5 rounded-xl ${sentInfo.status === 'sent' ? 'bg-[#EFF6EF] text-[#3F6B3F]' : sentInfo.status === 'mocked' ? 'bg-[#FFF7EC] text-[#7B4A1A]' : 'bg-[#FDECEA] text-red-700'}`}>
                  {sentInfo.status === 'sent' && `Sent successfully — reply will appear in the Messages tab.`}
                  {sentInfo.status === 'mocked' && `Logged locally, but ${ch === 'email' ? 'Postmark' : 'SMS provider'} isn't configured on the server.`}
                  {sentInfo.status === 'failed' && `Send failed: ${sentInfo.error}`}
                </div>
              )}
            </>
          )}
        </div>
        {!busy && text && (
          <div className="p-3 border-t border-[#EFE3D8] flex items-center justify-between gap-2 bg-white">
            <button onClick={copy} data-testid="vendor-ai-copy" className="text-xs px-3 py-2 rounded-full border border-[#EFE3D8] inline-flex items-center gap-1.5">
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
            <button onClick={send} disabled={sending || !canSend} data-testid="vendor-ai-send"
                    className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {sending ? 'Sending…' : `Send ${ch === 'email' ? 'email' : 'SMS'}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ComparePanel({ result, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between">
          <h2 className="font-serif text-lg text-[#2A2A2A] inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#7B2C3B]" /> AI comparison</h2>
          <button onClick={onClose} className="text-[#9A8A7E] hover:text-[#7B2C3B]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            {(result.vendors || []).map(v => (
              <span key={v.id} className="text-xs px-2.5 py-1 rounded-full bg-[#F4E4DA] text-[#7B2C3B] border border-[#EBD3C2]">{v.name} <span className="text-[#9A8A7E]">· ${(v.total || 0).toLocaleString()}</span></span>
            ))}
          </div>
          <p className="text-sm text-[#2A2A2A] leading-relaxed whitespace-pre-wrap" data-testid="vendor-compare-summary">{result.summary}</p>
        </div>
      </div>
    </div>
  );
}

// ---------- Auto-action modal (fires after drag between columns) ----------
function AutoActionModal({ state, onClose, onOpenEditor, onOpenAI, onDone }) {
  const { vendor, kind, prompt } = state;
  const [questions, setQuestions] = useState(null);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const generateFollowups = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/vendors/${vendor.id}/ai/followup-questions`);
      setQuestions(data.questions || []);
    } catch { toast({ title: 'AI failed — try again', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const copyQuestions = () => {
    navigator.clipboard.writeText((questions || []).map((q, i) => `${i + 1}. ${q}`).join('\n'));
    toast({ title: 'Copied — paste into a reply email' });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="vendor-auto-action">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#7B2C3B]">Moved to {vendor.status}</p>
            <h2 className="font-serif text-lg text-[#2A2A2A]">{vendor.name}</h2>
          </div>
          <button onClick={onClose} className="text-[#9A8A7E] hover:text-[#7B2C3B]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-[#3A3A3A]">{prompt}</p>

          {kind === 'offer_first_contact' && (
            <button onClick={() => onOpenAI(vendor)} data-testid="auto-action-write-first"
                    className="btn-primary text-xs px-4 py-2 rounded-full w-full inline-flex items-center justify-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Write & send first message
            </button>
          )}

          {kind === 'generate_followup' && (
            <>
              {!questions ? (
                <button onClick={generateFollowups} disabled={busy} data-testid="auto-action-gen-followup" className="btn-primary text-xs px-4 py-2 rounded-full w-full inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {busy ? 'Thinking…' : 'Generate 5 follow-up questions'}
                </button>
              ) : (
                <>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-[#2A2A2A]">
                    {questions.map((qq, i) => <li key={i}>{qq}</li>)}
                  </ol>
                  <div className="flex gap-2 pt-2">
                    <button onClick={copyQuestions} className="text-xs px-3 py-2 rounded-full border border-[#EFE3D8] inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                    <button onClick={() => onOpenAI(vendor)} className="btn-primary text-xs px-3 py-2 rounded-full inline-flex items-center gap-1"><Send className="w-3 h-3" /> Send as email</button>
                  </div>
                </>
              )}
            </>
          )}

          {kind === 'log_quote' && (
            <button onClick={() => onOpenEditor(vendor.id)} data-testid="auto-action-log-quote"
                    className="btn-primary text-xs px-4 py-2 rounded-full w-full inline-flex items-center justify-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Log the quote & check budget fit
            </button>
          )}

          {kind === 'confirm_booked' && (
            <button onClick={() => onOpenEditor(vendor.id)} data-testid="auto-action-open-editor"
                    className="btn-primary text-xs px-4 py-2 rounded-full w-full inline-flex items-center justify-center gap-1.5">
              <Upload className="w-3.5 h-3.5" /> Upload contract + record deposit
            </button>
          )}

          {kind === 'log_payment' && (
            <button onClick={() => onOpenEditor(vendor.id)} data-testid="auto-action-log-payment"
                    className="btn-primary text-xs px-4 py-2 rounded-full w-full inline-flex items-center justify-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Log the final payment
            </button>
          )}

          <button onClick={onClose} className="text-xs text-[#9A8A7E] hover:text-[#2A2A2A] w-full text-center">Skip for now</button>
        </div>
      </div>
    </div>
  );
}

// ---------- AI Vendor Discovery ----------
function DiscoverModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ category: 'Florist', location: '', style: '', budget: '', count: 6 });
  const [budgetCats, setBudgetCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [added, setAdded] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [p, b] = await Promise.all([
          client.get('/profile'),
          client.get('/budget'),
        ]);
        if (!form.location && p.data?.location) setForm((f) => ({ ...f, location: p.data.location }));
        setBudgetCats((b.data.categories || []).map(c => c.name));
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    if (!form.location.trim()) { toast({ title: 'Enter a location', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const { data } = await client.post('/vendors/discover', form);
      setResults(data);
    } catch (e) {
      toast({ title: 'Discovery failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const addOne = async (v, idx) => {
    setAdded((a) => ({ ...a, [idx]: 'loading' }));
    try {
      const budget_category = budgetCats.find(c => c.toLowerCase() === form.category.toLowerCase()) || '';
      await client.post('/vendors/from-discovery', {
        name: v.name, website: v.website, location: v.location,
        price_range: v.price_range, why_it_fits: v.why_it_fits, instagram: v.instagram,
        category: form.category, budget_category,
      });
      setAdded((a) => ({ ...a, [idx]: 'done' }));
    } catch (e) {
      setAdded((a) => ({ ...a, [idx]: 'error' }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="vendor-discover-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between">
          <div>
            <h2 className="font-serif text-lg text-[#2A2A2A] inline-flex items-center gap-2"><Compass className="w-4 h-4 text-[#7B2C3B]" /> Discover wedding vendors</h2>
            <p className="text-xs text-[#6A6A6A]">Live web-search + AI picks — add any to your Researching column with one tap.</p>
          </div>
          <button onClick={onClose} className="text-[#9A8A7E] hover:text-[#7B2C3B]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b border-[#EFE3D8] bg-[#FBF7F2] grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="block col-span-2 md:col-span-1"><span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Category</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input mt-1" data-testid="discover-cat">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block col-span-2"><span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Location</span>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input mt-1" placeholder="Newport Beach, CA" data-testid="discover-location" />
          </label>
          <label className="block col-span-2 md:col-span-1"><span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">How many?</span>
            <input type="number" min={3} max={10} value={form.count} onChange={(e) => setForm({ ...form, count: Number(e.target.value) || 6 })} className="input mt-1" />
          </label>
          <label className="block col-span-2"><span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Style / vibe (optional)</span>
            <input value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} className="input mt-1" placeholder="Garden, boho, elegant modern…" />
          </label>
          <label className="block col-span-2"><span className="text-[10px] uppercase tracking-widest text-[#9A8A7E]">Budget range (optional)</span>
            <input value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="input mt-1" placeholder="$$ or $2-5k" />
          </label>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {!results && !busy && (
            <button onClick={run} data-testid="discover-run" className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5">
              <Compass className="w-3.5 h-3.5" /> Find vendors
            </button>
          )}
          {busy && <p className="text-sm text-[#6A6A6A] py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Searching the web + ranking with AI…</p>}
          {results && (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] text-[#9A8A7E]">
                  {results.source === 'live_search' ? `Live search · scanned ${results.hits_scanned} results` : 'AI knowledge base'}
                </p>
                <button onClick={run} className="text-xs text-[#7B2C3B] hover:underline">↻ Search again</button>
              </div>
              {(results.vendors || []).length === 0 ? (
                <p className="text-sm text-[#6A6A6A] italic text-center py-8">No good matches — try a different location or style.</p>
              ) : (
                <ul className="space-y-3">
                  {results.vendors.map((v, i) => (
                    <li key={i} className="border border-[#EFE3D8] rounded-xl p-3" data-testid={`discover-result-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-[#2A2A2A]">{v.name}</p>
                          <p className="text-xs text-[#6A6A6A]">{v.location}{v.price_range ? ` · ${v.price_range}` : ''}{v.instagram ? ` · IG ${v.instagram}` : ''}</p>
                          {v.why_it_fits && <p className="text-xs text-[#3A3A3A] mt-1 italic">"{v.why_it_fits}"</p>}
                          {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="text-xs text-[#7B2C3B] hover:underline inline-flex items-center gap-1 mt-1"><Globe className="w-3 h-3" /> {v.website.replace(/^https?:\/\//, '').slice(0, 42)}</a>}
                        </div>
                        <button onClick={() => addOne(v, i)} disabled={added[i] === 'done' || added[i] === 'loading'}
                                data-testid={`discover-add-${i}`}
                                className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap ${added[i] === 'done' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'btn-primary'}`}>
                          {added[i] === 'loading' ? <Loader2 className="w-3 h-3 animate-spin" /> : added[i] === 'done' ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                          {added[i] === 'done' ? 'Added' : added[i] === 'loading' ? 'Adding…' : 'Add'}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        <div className="p-3 border-t border-[#EFE3D8] flex items-center justify-end bg-white">
          <button onClick={() => { onClose(); if (Object.values(added).some(v => v === 'done')) onAdded?.(); }} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8]">Done</button>
        </div>
      </div>
      <style>{`.input{width:100%;padding:0.5rem 0.75rem;border-radius:0.75rem;border:1px solid #EFE3D8;font-size:0.875rem;background:#FBF7F2}.input:focus{outline:none;border-color:#7B2C3B}`}</style>
    </div>
  );
}
